<template>
  <div class="cb-data-charts">
    <PlaceholderComponent v-if="shouldDisplayPlaceholderComponent(isEditMode, mode, visible)" :name="tracking_id" />
    <EditableComponent
      v-else-if="isEditMode && !isViewOnlyMode && componentPermissions.hasSomethingEditable && canEditChart"
      :id="id"
      :active="componentEditMode && componentEditMode?.id === id"
      :duplicate="componentPermissions.canDuplicate"
      :edit="componentPermissions.canEdit"
      :remove="componentPermissions.canRemove"
      :selected="componentSelectedMode && componentSelectedMode?.id === id"
      :settings="!usedInSectionId"
      :stylable="!usedInSectionId"
      @duplicate="duplicate(id)"
      @edit="setComponentEditMode(ComponentTypes.DataCharts, id)"
      @remove="removeComponentById(id)"
      @select="setComponentSelectedMode(id)"
      @settings="setComponentSettingsMode(id)"
      @style="emit('style', $event)"
    >
      <RawDataCharts v-if="!isEmpty(data)" :key="key" class="ma-1" :data="data" is-edit-mode :style="style" />
      <EditDataCharts v-else :id="id" :style="style" />
    </EditableComponent>
    <RawDataCharts v-else :data="data" :is-edit-mode="isEditMode" :style="style" />
    <DataChartsSettings v-if="componentEditMode && componentEditMode.id === id" :id="id" :data="data" />
  </div>
</template>

<script setup lang="ts">
import { isEmpty } from 'lodash-es'
import { computed, onMounted, toRefs } from 'vue'

import EditableComponent from '@canvas-builder/components/container/EditableComponent/EditableComponent.vue'
import EditDataCharts from './DataCharts.edit.vue'
import RawDataCharts from './DataCharts.raw.vue'
import DataChartsSettings from './DataCharts.settings.vue'

import PlaceholderComponent from '@canvas-builder/components/container/PlaceholderComponent/PlaceholderComponent.vue'
import useCanvas from '@canvas-builder/composables/useCanvas'
import useComponentPermissions from '@canvas-builder/composables/useComponentPermissions'
import { defaultWhenUsedInSection } from '@canvas-builder/constants'
import { CanvasBuilderMode, ComponentTypes, type ComponentNode } from '@canvas-builder/types/canvas'
import { shouldDisplayPlaceholderComponent } from '@canvas-builder/util/canvas.util'
import { type CanvasRetrieve } from '@openapi'
import type { ExtendedChartConfiguration } from '@canvas-builder/components/ui/DataCharts/types'

const props = withDefaults(
  defineProps<{
    id: ComponentNode['id']
    data: ExtendedChartConfiguration
    children?: ComponentNode[]
    usedInSectionId?: CanvasRetrieve['id']
    when_used_in_section?: ComponentNode['when_used_in_section']
    visible?: ComponentNode['visible']
    tracking_id?: string
    style?: Record<string, any>
  }>(),
  {
    children: () => [],
    when_used_in_section: () => defaultWhenUsedInSection,
    usedInSectionId: '',
  },
)

const emit = defineEmits<{
  style: [s: Record<string, any>, id?: ComponentNode['id']]
}>()

const key = computed(() => {
  return JSON.stringify(props.data)
})

const { id, usedInSectionId, when_used_in_section } = toRefs(props)

const {
  componentEditMode,
  componentSelectedMode,
  duplicate,
  isEditMode,
  isViewOnlyMode,
  justAddedComponentId,
  mode,
  removeComponentById,
  setComponentEditMode,
  setComponentSelectedMode,
  setComponentSettingsMode,
} = useCanvas()

const componentPermissions = useComponentPermissions({
  id,
  usedInSectionId,
  when_used_in_section,
})

// In Impact mode, check if data entry is allowed by the template setting
// In Admin mode, always allow editing
const canEditChart = computed(() => {
  if (mode.value === CanvasBuilderMode.ADMIN) {
    return true
  }
  // In Impact mode, only allow editing unless enable_data_entry is explicitly false
  return props.data?.view_controls?.enable_data_entry !== false
})

onMounted(() => {
  if (justAddedComponentId.value === props.id) {
    setComponentEditMode(ComponentTypes.DataCharts, props.id)
    justAddedComponentId.value = null
  }
})
</script>
